/**
 * One config field, rendered with its provenance (YAG-371).
 *
 * The pane's whole claim is that a setting always says where it came from, so
 * a field never renders without its chip:
 *   - `platform` / `workspace` ⇒ a dashed "inherited · …" chip. Nothing was
 *     set here; changing it here creates this Team's delta.
 *   - `team` ⇒ an accent "set here" chip and, right beside it, the way back.
 *     Reset DELETES the delta, so the value returns to whatever it inherits
 *     rather than being frozen at today's inherited value.
 *
 * The controls are native elements on purpose. A settings row is not a gate,
 * so it gets no glass and no ceremony; the fastest honest control wins.
 */
import { useState } from "react";
import type { WorkerConfigField, WorkerConfigSource } from "@yagni/shared";
import { Chip } from "../ui/index.js";
import { PROVENANCE_CHIP, RESET_LABEL } from "./copy.js";

export interface ConfigControlProps {
  field: WorkerConfigField;
  value: unknown;
  /**
   * Where the value came from. Omitted off the Workers surface (the repository
   * environment editor has no sparse-delta chain), and then no provenance line
   * and no reset render — the control alone.
   */
  source?: WorkerConfigSource;
  disabled?: boolean;
  onChange: (value: unknown) => void;
  onReset?: () => void;
  /** `author_list` / `repo_list`: the datalist options — observed PR logins
   * for authors, the GitHub App installation's repos for repositories. */
  suggestions?: string[];
}

/** A text_list renders as one comma-separated line, which is how people say it. */
export function listToText(value: unknown): string {
  return Array.isArray(value) ? value.map((entry) => String(entry)).join(", ") : "";
}

export function textToList(text: string): string[] {
  return text
    .split(",")
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}

/**
 * Where a value came from, as a quiet line rather than a badge.
 *
 * The dashed chip read as a state ("this field is in a dashed mode") when all
 * it ever meant was provenance. A dot plus a phrase says the same thing without
 * competing with the control beside it: grey dot = inherited, accent dot = set
 * on this Team. The copy is still `PROVENANCE_CHIP`, so all three levels
 * (platform / workspace / team) keep their canonical wording.
 *
 * The name is kept for its eight call sites; it is no longer a `Chip`.
 */
export function ProvenanceChip({ source }: { source: WorkerConfigSource }) {
  return (
    <span
      className={`wrk-source wrk-source--${source}`}
      data-testid={`wrk-provenance-${source}`}
    >
      <span className="wrk-source-dot" aria-hidden="true" />
      {PROVENANCE_CHIP[source]}
    </span>
  );
}

/**
 * A picked chip's value list, tolerating the legacy shape: `repo_list` rows
 * stored while the field was a multiline `text` hold a newline-separated
 * string. Reading both means an old delta renders as chips instead of
 * vanishing; the first edit rewrites it as an array.
 */
export function pickedEntries(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((entry) => String(entry));
  if (typeof value === "string") {
    return value
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
  }
  return [];
}

/**
 * The picked-chips control (`author_list`, `repo_list`): one chip per picked
 * entry, a draft input that commits on Enter or blur, and native datalist
 * suggestions (the SettingsView timezone precedent) — observed PR logins for
 * authors, the GitHub App installation's repositories for repos. Picking a
 * canonical entry sidesteps the spelling trap for good; hand-typed values are
 * still caught by the backend's normalization.
 */
function PickedListControl({
  id,
  field,
  value,
  disabled,
  onChange,
  suggestions,
}: {
  id: string;
  field: WorkerConfigField;
  value: unknown;
  disabled?: boolean;
  onChange: (value: unknown) => void;
  suggestions?: string[];
}) {
  const [draft, setDraft] = useState("");
  const picked = pickedEntries(value);
  const commit = (raw: string) => {
    const entry = raw.trim();
    setDraft("");
    if (entry.length === 0 || picked.includes(entry)) return;
    onChange([...picked, entry]);
  };
  return (
    <span className="wrk-authors">
      {picked.map((entry) => (
        <Chip key={entry} tone="neutral">
          {entry}
          <button
            type="button"
            className="wrk-chip-remove"
            aria-label={`Remove ${entry}`}
            disabled={disabled}
            onClick={() => onChange(picked.filter((kept) => kept !== entry))}
          >
            ×
          </button>
        </Chip>
      ))}
      <input
        id={id}
        type="text"
        className="wrk-control"
        aria-label={field.label}
        list={`${id}-suggestions`}
        value={draft}
        disabled={disabled}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            commit(draft);
          }
        }}
        onBlur={() => commit(draft)}
      />
      <datalist id={`${id}-suggestions`}>
        {(suggestions ?? [])
          .filter((login) => !picked.includes(login))
          .map((login) => (
            <option key={login} value={login} />
          ))}
      </datalist>
    </span>
  );
}

export function ConfigControl({
  field,
  value,
  source,
  disabled,
  onChange,
  onReset,
  suggestions,
}: ConfigControlProps) {
  const id = `wrk-config-${field.key}`;
  return (
    <span className="wrk-srow-controls">
      {(field.type === "author_list" || field.type === "repo_list") && (
        <PickedListControl
          id={id}
          field={field}
          value={value}
          disabled={disabled}
          onChange={onChange}
          suggestions={suggestions}
        />
      )}
      {field.type === "boolean" && (
        <input
          id={id}
          type="checkbox"
          aria-label={field.label}
          checked={value === true}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
        />
      )}
      {field.type === "number" && (
        <input
          id={id}
          type="number"
          className="wrk-control"
          aria-label={field.label}
          value={typeof value === "number" ? value : ""}
          min={field.min}
          max={field.max}
          disabled={disabled}
          onChange={(event) => {
            const next = Number(event.target.value);
            if (Number.isFinite(next)) onChange(next);
          }}
        />
      )}
      {field.type === "select" && (
        <select
          id={id}
          className="wrk-control"
          aria-label={field.label}
          value={typeof value === "string" ? value : ""}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
        >
          {(field.options ?? []).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}
      {/* A field people answer in sentences (say, what counts as urgent) gets a
          box that holds them, instead of one line that scrolls sideways. */}
      {field.type === "text" && field.multiline === true && (
        <textarea
          id={id}
          className="wrk-control wrk-control--multiline"
          aria-label={field.label}
          rows={3}
          defaultValue={String(value ?? "")}
          disabled={disabled}
          onBlur={(event) => onChange(event.target.value)}
        />
      )}
      {((field.type === "text" && field.multiline !== true) ||
        field.type === "text_list") && (
        <input
          id={id}
          type="text"
          className="wrk-control"
          aria-label={field.label}
          defaultValue={
            field.type === "text_list" ? listToText(value) : String(value ?? "")
          }
          disabled={disabled}
          onBlur={(event) =>
            onChange(
              field.type === "text_list"
                ? textToList(event.target.value)
                : event.target.value,
            )
          }
        />
      )}
      {source && <ProvenanceChip source={source} />}
      {source === "team" && onReset && (
        <button
          type="button"
          className="wrk-quiet-btn"
          disabled={disabled}
          onClick={onReset}
          data-testid={`wrk-reset-${field.key}`}
        >
          {RESET_LABEL}
        </button>
      )}
    </span>
  );
}
